import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { PhoneField } from '@/components/ui/money-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const PAY_MODELS = [
  { value: 'percent', label: 'Percentage of Load' },
  { value: 'mileage', label: 'Per Mile' },
  { value: 'salary', label: 'Salary' },
  { value: 'owner_op', label: 'Owner Operator' },
];

const emptyForm = {
  name: '',
  phone: '',
  email: '',
  payModel: 'percent',
  percentage: '70',
  mileageRate: '',
  salary: '',
  feeLabel: 'Dispatch fee',
  feeAmount: '150',
};

function payBody(form) {
  return {
    name: form.name.trim(),
    phone: form.phone || undefined,
    email: form.email || undefined,
    payModel: form.payModel,
    defaultDriverPct: form.percentage ? Number(form.percentage) / 100 : undefined,
    defaultRatePerMile: form.mileageRate ? Number(form.mileageRate) : undefined,
    defaultSalaryAmount: form.salary ? Number(form.salary) : undefined,
    ownerOpFeeTemplate:
      form.payModel === 'owner_op' && form.feeAmount
        ? [{ label: form.feeLabel || 'Fee', amount: Number(form.feeAmount) || 0 }]
        : form.payModel === 'owner_op'
          ? []
          : undefined,
  };
}

function getPayDisplay(d) {
  switch (d.payModel) {
    case 'percent':
      return d.defaultDriverPct != null ? `${Math.round(d.defaultDriverPct * 100)}%` : '—';
    case 'mileage':
      return d.defaultRatePerMile != null ? `$${d.defaultRatePerMile}/mi` : '—';
    case 'salary':
      return d.defaultSalaryAmount != null ? `$${d.defaultSalaryAmount}` : '—';
    case 'owner_op':
      return 'Owner Op';
    default:
      return '—';
  }
}

export default function DriversPage() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [deactivateId, setDeactivateId] = useState('');

  const driversQ = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const r = await api('/api/v1/drivers');
      if (r.status !== 200) throw new Error(r.data?.error ?? 'Failed to load drivers');
      return r.data.drivers ?? [];
    },
  });

  const drivers = driversQ.data ?? [];
  const selected = drivers.find((d) => d.id === selectedId);
  const deactivateTarget = drivers.find((d) => d.id === deactivateId);

  useEffect(() => {
    if (!selected) return;
    setEditForm({
      name: selected.name ?? '',
      phone: selected.phone ?? '',
      email: selected.email ?? '',
      payModel: selected.payModel ?? 'percent',
      percentage: String(Math.round((selected.defaultDriverPct ?? 0.7) * 100)),
      mileageRate: selected.defaultRatePerMile != null ? String(selected.defaultRatePerMile) : '',
      salary: selected.defaultSalaryAmount != null ? String(selected.defaultSalaryAmount) : '',
      feeLabel: selected.ownerOpFeeTemplate?.[0]?.label ?? 'Dispatch fee',
      feeAmount: selected.ownerOpFeeTemplate?.[0]
        ? String(selected.ownerOpFeeTemplate[0].amount)
        : '150',
    });
  }, [selectedId, selected]);

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'phone', label: 'Phone', className: 'font-mono', render: (v) => v || '—' },
    { key: 'email', label: 'Email', render: (v) => v || '—' },
    {
      key: 'payModel',
      label: 'Pay Model',
      render: (v) => (
        <StatusPill status="active">
          {PAY_MODELS.find((p) => p.value === v)?.label || v || '—'}
        </StatusPill>
      ),
    },
    {
      key: 'payValue',
      label: 'Rate',
      className: 'font-mono',
      render: (_, row) => getPayDisplay(row),
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'id',
      label: '',
      render: (id, row) => (
        <div className="flex items-center gap-1 justify-end">
          <Button size="sm" variant="outline" onClick={() => setSelectedId(id)}>
            Edit
          </Button>
          {row.status !== 'inactive' ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeactivateId(id)}
            >
              Deactivate
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const handleAdd = async () => {
    if (!form.name || !form.payModel) {
      toast.error('Name and pay model are required');
      return;
    }
    const r = await api('/api/v1/drivers', {
      method: 'POST',
      body: payBody(form),
    });
    if (r.status === 201) {
      toast.success(`Driver ${form.name} added`);
      setForm(emptyForm);
      setShowAdd(false);
      qc.invalidateQueries({ queryKey: ['drivers'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    const r = await api(`/api/v1/drivers/${selectedId}`, {
      method: 'PATCH',
      body: {
        ...payBody(editForm),
        phone: editForm.phone,
        email: editForm.email,
        ownerOpFeeTemplate:
          editForm.payModel === 'owner_op'
            ? [{ label: editForm.feeLabel || 'Fee', amount: Number(editForm.feeAmount) || 0 }]
            : [],
      },
    });
    if (r.status === 200) {
      toast.success('Driver saved');
      setSelectedId('');
      qc.invalidateQueries({ queryKey: ['drivers'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateId) return;
    const r = await api(`/api/v1/drivers/${deactivateId}`, { method: 'DELETE' });
    if (r.status === 200) {
      toast.success('Driver deactivated');
      if (selectedId === deactivateId) setSelectedId('');
      setDeactivateId('');
      qc.invalidateQueries({ queryKey: ['drivers'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Drivers"
          description="Roster and pay defaults — edit details anytime; deactivate drivers who leave without breaking past settlements."
          icon="UserRound"
          metrics={[
            { label: 'Total', value: drivers.length },
            { label: 'Active', value: drivers.filter((d) => d.status === 'active').length },
          ]}
          actions={
            <Button onClick={() => setShowAdd(!showAdd)} data-testid="add-driver-button">
              <Icons.Plus className="h-4 w-4 mr-2" />
              Add Driver
            </Button>
          }
        />

        {showAdd && (
          <Card>
            <CardHeader>
              <CardTitle>New Driver</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingField
                  label="Full Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="driver-name-input"
                />
                <PhoneField
                  label="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
                <FloatingField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Pay Model <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={form.payModel}
                    onValueChange={(v) => setForm({ ...form, payModel: v })}
                  >
                    <SelectTrigger data-testid="driver-pay-model-select">
                      <SelectValue placeholder="Select pay model" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAY_MODELS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(form.payModel === 'percent' || form.payModel === 'owner_op') && (
                <FloatingField
                  label="Default Percentage (%)"
                  type="number"
                  value={form.percentage}
                  onChange={(e) => setForm({ ...form, percentage: e.target.value })}
                />
              )}
              {form.payModel === 'mileage' && (
                <FloatingField
                  label="$ per mile"
                  type="number"
                  step="0.01"
                  value={form.mileageRate}
                  onChange={(e) => setForm({ ...form, mileageRate: e.target.value })}
                />
              )}
              {form.payModel === 'salary' && (
                <FloatingField
                  label="Salary Amount ($)"
                  type="number"
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                />
              )}
              {form.payModel === 'owner_op' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FloatingField
                    label="Fee Label"
                    value={form.feeLabel}
                    onChange={(e) => setForm({ ...form, feeLabel: e.target.value })}
                    placeholder="e.g., Lease Fee"
                  />
                  <FloatingField
                    label="Fee Amount ($)"
                    type="number"
                    value={form.feeAmount}
                    onChange={(e) => setForm({ ...form, feeAmount: e.target.value })}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAdd} data-testid="save-driver-button">
                  Add Driver
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedId && (
          <Card>
            <CardHeader>
              <CardTitle>Edit driver — {selected?.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingField
                  label="Full Name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Pay Model
                  </label>
                  <Select
                    value={editForm.payModel}
                    onValueChange={(v) => setEditForm({ ...editForm, payModel: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAY_MODELS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FloatingField
                  label="Default Percentage (%)"
                  type="number"
                  value={editForm.percentage}
                  onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })}
                />
                <FloatingField
                  label="$ per mile"
                  type="number"
                  step="0.01"
                  value={editForm.mileageRate}
                  onChange={(e) => setEditForm({ ...editForm, mileageRate: e.target.value })}
                />
                <FloatingField
                  label="Salary"
                  type="number"
                  value={editForm.salary}
                  onChange={(e) => setEditForm({ ...editForm, salary: e.target.value })}
                />
                {editForm.payModel === 'owner_op' && (
                  <>
                    <FloatingField
                      label="Fee Label"
                      value={editForm.feeLabel}
                      onChange={(e) => setEditForm({ ...editForm, feeLabel: e.target.value })}
                    />
                    <FloatingField
                      label="Fee Amount ($)"
                      type="number"
                      value={editForm.feeAmount}
                      onChange={(e) => setEditForm({ ...editForm, feeAmount: e.target.value })}
                    />
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedId('')}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>Save defaults</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <DataTable
          columns={columns}
          data={drivers}
          emptyMessage={driversQ.isLoading ? 'Loading…' : 'No drivers'}
        />

        <ConfirmDialog
          open={Boolean(deactivateId)}
          onOpenChange={(open) => !open && setDeactivateId('')}
          title="Deactivate driver?"
          description={`Mark ${deactivateTarget?.name || 'this driver'} inactive? They leave dispatch pickers; past settlements stay intact.`}
          confirmLabel="Deactivate"
          variant="destructive"
          onConfirm={handleDeactivate}
        />
      </div>
);
}
